import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  viewClient,
  ViewClient,
  ViewClientApiResponse,
} from '../api/viewClientApiService';
import {
  getStatusLabel,
  getStatusBadgeClasses,
} from '@/shared/utils/statusBadge';
import { CLIENT_LABELS } from '../constants/labels';

// ============================================================================
// Constants
// ============================================================================

export const CLIENT_PROFILE_NAV_ITEMS = [
  { id: 'BasicInformation', label: CLIENT_LABELS.VIEW_CLIENT_NAV_BASIC_INFO },
  { id: 'BillingDetails', label: CLIENT_LABELS.VIEW_CLIENT_NAV_BILLING },
  { id: 'ComplianceDetails', label: CLIENT_LABELS.VIEW_CLIENT_NAV_COMPLIANCE },
  { id: 'Facilities', label: CLIENT_LABELS.VIEW_CLIENT_NAV_FACILITIES },
  { id: 'Subscription', label: CLIENT_LABELS.VIEW_CLIENT_NAV_SUBSCRIPTION },
  { id: 'Permissions', label: CLIENT_LABELS.VIEW_CLIENT_NAV_PERMISSIONS },
  { id: 'Certificates', label: CLIENT_LABELS.VIEW_CLIENT_NAV_CERTIFICATES },
];

// ============================================================================
// Utility
// ============================================================================

export const getClientInitials = (name: string | null | undefined): string => {
  if (!name) {
    return '—';
  }
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// ============================================================================
// Hook
// ============================================================================

export interface UseViewClientReturn {
  clientData: ViewClientApiResponse | undefined;
  client: ViewClient | undefined;
  isLoading: boolean;
  showErrorScreen: boolean;
  accessError: boolean;
  initials: string;
  statusLabel: string;
  statusBadgeClasses: { container: string; dot: string };
  refetch: () => Promise<void>;
}

export const useViewClient = (clientId: string): UseViewClientReturn => {
  const [clientData, setClientData] = useState<
    ViewClientApiResponse | undefined
  >(undefined);
  const [showErrorScreen, setShowErrorScreen] = useState(false);
  const [accessError, setAccessError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchClientData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await viewClient(clientId);
      if (response.success && response.data) {
        setClientData(response.data);
        setShowErrorScreen(false);
      } else if (response.forbidden) {
        setAccessError(true);
      } else {
        setShowErrorScreen(true);
      }
    } catch {
      setShowErrorScreen(true);
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchClientData();
  }, [fetchClientData]);

  const client = useMemo(() => clientData?.client, [clientData]);

  const initials = useMemo(
    () => getClientInitials(client?.name),
    [client?.name]
  );

  const statusLabel = useMemo(
    () =>
      getStatusLabel(client?.status === 'draft' ? 'inactive' : client?.status),
    [client?.status]
  );

  const statusBadgeClasses = useMemo(
    () =>
      getStatusBadgeClasses(
        client?.status === 'draft' ? 'inactive' : client?.status
      ),
    [client?.status]
  );

  return {
    clientData,
    client,
    isLoading,
    showErrorScreen,
    accessError,
    initials,
    statusLabel,
    statusBadgeClasses,
    refetch: fetchClientData,
  };
};
